import type { ReactNode } from 'react'
import { TONE_CLASS, type Tone } from '@/lib/status'
import { cn } from '@/lib/utils'

export interface NoticeProps {
  tone: Tone
  /** One line: what is wrong. Takes the tone's colour. */
  title: ReactNode
  /** What it means and what to do, in body colour. */
  children?: ReactNode
  /** The service's own words, whole, in mono. */
  detail?: string
  /** `alert` for something the reader has to act on now; `status` (default)
   *  for a standing condition. */
  role?: 'alert' | 'status'
  className?: string
}

/** A standing condition on this page — a data-quality warning, the last pull
 *  that failed. The attention strip's rule: the card's own background, the
 *  tone only on the left stripe and the title, so a page never shouts in an
 *  amber or pink block. A failure of the action just taken stays an
 *  ErrorState. */
export function Notice({ tone, title, children, detail, role = 'status', className }: NoticeProps) {
  const c = TONE_CLASS[tone]
  return (
    <div role={role} className={cn('relative overflow-hidden rounded-lg border border-border bg-card py-3 pl-5 pr-4', className)}>
      <span aria-hidden className={cn('absolute inset-y-0 left-0 w-1', c.dot)} />
      <p className={cn('text-body font-medium', tone === 'neutral' ? 'text-foreground' : c.text)}>{title}</p>
      {children && <div className="mt-1 text-table text-muted-foreground">{children}</div>}
      {detail && <pre className="mt-1.5 whitespace-pre-wrap break-words font-mono text-meta text-foreground/80">{detail}</pre>}
    </div>
  )
}
